/**
 * English: Templates
 *
 * What this file covers: WhatsApp message templates and Meta's approval of them.
 *
 * Tone: Precise. Keep Meta's terms consistent (template, category, approved, rejected, paused). Explain Meta's
 * rules plainly, without blame.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const templates: Catalog = {
  'templates.title': 'Templates',
  'templates.view.overview.emptyBody': 'No body text yet',
  'templates.view.overview.metaStatus': 'Meta status',
  'templates.view.overview.metaNeverSeen': 'Meta has not seen this yet',
  'templates.view.overview.importedAt': 'Imported from Meta',
  'templates.view.overview.placeholderLabel': 'Placeholder {{position}}, replaced when you send',
  'templates.view.variables.detected': '{{count}} placeholders found',
  'templates.view.variables.label': 'Placeholder {{position}}',
  'templates.view.variables.placeholder': 'Example value for placeholder {{position}}',
  'templates.columns.code': 'Code',
  'templates.actions.for': 'Actions for {{code}}',
  'templates.actions.submit': 'Submit to Meta',
  'templates.actions.duplicate': 'Duplicate',
  'templates.actions.importFromMeta': 'Import from Meta',
  'templates.search.label': 'Search templates',
  'templates.search.placeholder': 'Search code, language or category',
  'templates.empty.hint': 'A template is a message Meta has approved in advance. You need one before you can broadcast.',
  'templates.emptyFiltered': 'No templates match',
  'templates.emptyFiltered.hint': 'Try another status or search term.',
  'templates.status.draft': 'Draft',
  'templates.status.submitted': 'With Meta',
  'templates.status.approved': 'Approved',
  'templates.status.rejected': 'Rejected',
  'templates.status.paused': 'Paused',
  'templates.category.utility': 'Utility',
  'templates.category.marketing': 'Marketing',
  'templates.category.authentication': 'Authentication',
  'templates.devFallback': 'Not really approved',
  'templates.devFallback.tooltip': 'Auto-approved by this environment, not by Meta. It will fail on a real send.',
  'templates.metaStatus.tooltip': 'Meta currently reports this as {{status}}.',
  'templates.drawer.new': 'New template',
  'templates.drawer.edit': 'Edit template',
  'templates.drawer.components': 'Components',
  'templates.drawer.preview': 'Preview',
  'templates.drawer.previewEmpty': 'Add a BODY component to see the message here.',
  'templates.drawer.format': 'Tidy up',
  'templates.drawer.languageLocked': 'The language is fixed once a template exists. Duplicate it to make another language.',
  'templates.drawer.marketingHint': 'Marketing templates may not contain a bare link. Use a URL button.',
  'templates.modal.submit.title': 'Send this to Meta for approval?',
  'templates.modal.submit.body': '{{code}} goes into Meta’s review queue, which usually takes a day or more.',
  'templates.modal.submit.irreversible': 'It cannot be edited while it is with Meta. If they reject it you can fix it and resubmit.',
  'templates.toast.submittedNamed': '{{code}} sent to Meta for approval.',
  'templates.toast.deleted': '{{code}} deleted.',
  'templates.toast.deleteFailed': 'Delete failed',
  'templates.toast.imported': '{{imported}} imported, {{updated}} updated, {{skipped}} skipped.',
  'templates.toast.importFailed': 'Import failed',
  'templates.import.noWaba': 'No WhatsApp Business Account is attached yet. Attach a number first.',
  'templates.import.notConfigured': 'Meta credentials are not set up. An administrator adds them in Settings → Integrations.',
  'templates.import.metaError': 'Meta refused the request. Check that the credentials are current.',
  'templates.validate.title': 'Meta will reject this as it stands',
  'templates.validate.invalidJson': 'The components are not valid JSON.',
  'templates.validate.notArray': 'Components must be a list.',
  'templates.validate.bodyMissing': 'A template needs a BODY component.',
  'templates.validate.repeated': 'A template may have only one {{type}} component.',
  'templates.validate.headerTooLong': 'The header is {{length}} characters; Meta allows {{max}}.',
  'templates.validate.bodyTooLong': 'The body is {{length}} characters; Meta allows {{max}}.',
  'templates.validate.footerTooLong': 'The footer is {{length}} characters; Meta allows {{max}}.',
  'templates.validate.startsWithVariable': 'The body may not start with a placeholder. Put some text in front of it.',
  'templates.validate.endsWithVariable': 'The body may not end with a placeholder. Put some text after it.',
  'templates.validate.marketingUrl': 'A marketing template may not contain a bare link in its body. Use a URL button instead.',
  'templates.validate.notFromOne': 'Placeholders must start at {{1}}.',
  'templates.validate.notContiguous': 'Placeholders must run 1, 2, 3 with no gaps; {{missing}} is missing.',
  'templates.toast.saved': 'Template {{code}} saved.',
  'templates.actions.saveDraft': 'Save draft',
  'templates.columns.category': 'Category',
  'templates.columns.note': 'Note',
  'templates.description': 'WhatsApp message templates. Draft a template, submit it to Meta for approval, then use it in broadcasts. Approved templates are immutable.',
  'templates.drawer.codeHint': 'Unique per language. Lowercase + underscores.',
  'templates.drawer.codeRequired': 'Code is required.',
  'templates.drawer.componentsHint': 'Array of { type, format?, text, buttons? }. {{1}} placeholders are filled at send time.',
  'templates.drawer.componentsInvalid': 'Components must be valid JSON.',
  'templates.empty': 'No templates yet.',
  'templates.loadError': 'Failed to load templates.',
  'templates.modal.delete.body': 'Delete template {{code}}? This cannot be undone.',
  'templates.modal.delete.title': 'Delete template?',
  'templates.toast.submitFailed': 'Submit failed',
  'templates.toast.submitted': 'Submitted to Meta for review.',
  'templates.view.backToAll': 'All templates',
  'templates.view.components.copied': 'Components JSON copied.',
  'templates.view.lifecycle.draftedAt': 'Drafted',
  'templates.view.lifecycle.metaId': 'Meta template id',
  'templates.view.lifecycle.metaName': 'Meta template name',
  'templates.view.lifecycle.metaSync': 'Meta sync',
  'templates.view.lifecycle.modal.submit.body': 'Once submitted, this template enters Meta\'s review queue. Approval typically takes 24 hours but can be longer for marketing templates.',
  'templates.view.lifecycle.modal.submit.title': 'Submit for Meta approval?',
  'templates.view.lifecycle.submitFailed': 'Failed to submit template.',
  'templates.view.lifecycle.submitHint': 'Submit this template for Meta review. Approved templates can be used in broadcasts.',
  'templates.view.lifecycle.submittedAt': 'Submitted to Meta',
  'templates.view.lifecycle.title': 'Meta review',
  'templates.view.notFound': 'Template not found.',
  'templates.view.overview.metadata': 'Metadata',
  'templates.view.overview.preview': 'Preview',
  'templates.view.overview.previewHint': 'Closest render to what Meta will deliver. Variable positions are highlighted in amber.',
  'templates.view.tabs.components': 'Components',
  'templates.view.tabs.variables': 'Variables',
  'templates.view.variables.copied': 'Filled body copied to clipboard.',
  'templates.view.variables.filled': 'Filled preview',
  'templates.view.variables.hint': 'Enter sample values to preview how the rendered body will look. Values stay client-side.',
  'templates.view.variables.none': 'This template has no {{N}} variables: it sends a static body.',
  'templates.banner': 'Templates must be approved by Meta before a broadcast can use them.',
  'templates.actions.create': 'Create template',
  'templates.banner.rejected': '{{count}} template(s) were rejected. The note column says why.',
  'templates.banner.rejected.action': 'Show them',
  'templates.filters.status.any': 'All statuses ({{count}})',
  'templates.showingOf': 'Showing {{count}} of {{total}} templates',
  'templates.note.devFallback': 'Approved by the developer fallback, not by Meta.',
  'templates.note.underReview': 'Under review by Meta.',
  'templates.note.paused': 'Paused by Meta for quality.',
  'templates.import.title': 'Import templates from Meta',
  'templates.import.body': 'Pull the templates that already exist on your WhatsApp Business Account and keep them in sync.',
  'templates.view.overview.businessName': 'Your business',
  'templates.view.overview.sampleTime': '11:30',
  'templates.view.overview.composer': 'Type a message',
  'templates.header.label': 'Header',
  'templates.header.hint': 'A template may have one header: a line of text, or an image, video or document.',
  'templates.header.format.none': 'No header',
  'templates.header.format.text': 'Text',
  'templates.header.format.image': 'Image',
  'templates.header.format.video': 'Video',
  'templates.header.format.document': 'Document',
  'templates.header.text': 'Header text',
  'templates.header.text.hint': 'Up to 60 characters. One variable is allowed.',
  'templates.header.sample': 'Sample media URL',
  'templates.header.sample.hint': 'Meta reviews this example. A media template submitted without one is rejected.',
  'templates.header.sendHint': 'This sample is for approval only. Each broadcast supplies its own media when it sends.',
  'templates.header.noSample': 'No sample media yet',
  'templates.validate.headerNoSample': 'A {{format}} header needs a sample media URL: Meta rejects the template without one.',
  'templates.validate.headerMediaWithText': 'A media header cannot also carry text. Remove the text, or change the header to Text.',
  'templates.validate.headerTextEmpty': 'A text header needs some text.',
  'templates.ways.library': 'Start from the library',
  'templates.ways.library.body': 'Pick a message that is already written: order confirmations, delivery updates, appointment and payment reminders. You change the wording afterwards if you want to.',
  'templates.ways.library.action': 'Browse the library',
  'templates.ways.import': 'Bring in what you already have',
  'templates.ways.import.body': 'If your WhatsApp account already has approved templates, copy them across instead of typing them again. Nothing on WhatsApp is changed.',
  'templates.ways.blank': 'Write your own',
  'templates.ways.blank.body': 'Start from nothing and write the message yourself, with a preview of the handset beside you as you type.',
  'templates.ways.blank.action': 'Start writing',
  'templates.wizard.title.new': 'New template',
  'templates.wizard.title.edit': 'Edit template',
  'templates.wizard.description.new': 'Four steps: where to start, what to call it, what it says, and one last look before WhatsApp sees it.',
  'templates.wizard.description.edit': 'Only a draft or a rejected template can be changed. Once WhatsApp approves it, the wording is fixed.',
  'templates.wizard.step.start': 'Start',
  'templates.wizard.step.start.hint': 'Library, import or blank',
  'templates.wizard.step.basics': 'Basics',
  'templates.wizard.step.basics.hint': 'Name, language, category',
  'templates.wizard.step.content': 'Content',
  'templates.wizard.step.content.hint': 'The message itself',
  'templates.wizard.step.review': 'Review',
  'templates.wizard.step.review.hint': 'Check, then send',
  'templates.wizard.back': 'Back',
  'templates.wizard.next': 'Next',
  'templates.wizard.way.library': 'Start from the library',
  'templates.wizard.way.library.body': 'Messages that are already written, and in WhatsApp\'s case already reviewed, so approval is usually quicker.',
  'templates.wizard.way.library.action': 'Browse',
  'templates.wizard.way.import': 'Bring in what you already have',
  'templates.wizard.way.import.body': 'Copy the templates already on your WhatsApp account across to here. Nothing on WhatsApp is changed.',
  'templates.wizard.way.import.action': 'Import now',
  'templates.wizard.way.blank': 'Write your own',
  'templates.wizard.way.blank.body': 'Start from nothing, with a preview of the handset beside you as you type.',
  'templates.wizard.way.blank.action': 'Start writing',
  'templates.wizard.name': 'Name',
  'templates.wizard.name.hint': 'Only lower-case letters, digits and underscores. This is how you will refer to the template when you send it.',
  'templates.wizard.name.willBecome': 'This will be saved as {{name}}, because WhatsApp allows only lower-case letters, digits and underscores.',
  'templates.wizard.name.ok': 'This is a name WhatsApp will accept.',
  'templates.wizard.name.required': 'Give the template a name.',
  'templates.wizard.language.hint': 'WhatsApp files a template under its name and language together, and neither can be changed afterwards. Duplicate the template to add a second language.',
  'templates.wizard.language.locked': 'The language cannot be changed. Duplicate this template to make a version in another language.',
  'templates.wizard.category.hint': 'The category decides what each message costs and how strictly WhatsApp reviews it. Choosing marketing for a receipt, or utility for an advert, gets the template refused.',
  'templates.wizard.category.utility.what': 'Something the customer asked for or is expecting: an order confirmation, a delivery update, a reminder about a booking.',
  'templates.wizard.category.utility.cost': 'Cheapest to send',
  'templates.wizard.category.marketing.what': 'Anything promotional: a special, a new arrival, a message asking somebody to come back.',
  'templates.wizard.category.marketing.cost': 'Costs more, and needs the customer to have opted in',
  'templates.wizard.category.authentication.what': 'One-time codes for signing in or confirming a payment. Nothing else.',
  'templates.wizard.category.authentication.cost': 'Priced separately',
  'templates.wizard.category.authentication.warning': 'An authentication template may contain only a one-time code and the words around it. WhatsApp refuses anything that looks like a normal message in this category.',
  'templates.wizard.content.title': 'The message',
  'templates.wizard.content.editJson': 'Edit as JSON',
  'templates.wizard.content.editForm': 'Back to the form',
  'templates.wizard.content.json': 'Components',
  'templates.wizard.content.jsonHint': 'WhatsApp\'s own components format. Whatever is here is exactly what gets sent for review.',
  'templates.wizard.content.advancedOnly': 'This template has parts the form cannot show, so it stays in the JSON editor. Nothing is lost that way.',
  'templates.wizard.header.sample': 'Sample for the heading',
  'templates.wizard.header.sample.hint': 'What WhatsApp should see in place of the placeholder when it reviews the heading.',
  'templates.wizard.body': 'Message',
  'templates.wizard.body.hint': 'Keep it short and say who you are. WhatsApp will not accept a message that begins or ends with a placeholder.',
  'templates.wizard.body.placeholder': 'Hi there, thank you for your order.',
  'templates.wizard.count': '{{length}} of {{max}}',
  'templates.wizard.addPlaceholder': 'Add a placeholder',
  'templates.wizard.samples': 'Sample values',
  'templates.wizard.samples.hint': 'WhatsApp reads the template with these in place of the placeholders when it reviews it, so use real-looking examples.',
  'templates.wizard.samples.for': 'Sample for placeholder {{n}}',
  'templates.wizard.samples.placeholder': 'For example: Thabo',
  'templates.wizard.footer': 'Footer',
  'templates.wizard.footer.hint': 'A small line under the message, at most 60 characters. Placeholders are not allowed here.',
  'templates.wizard.buttons': 'Buttons',
  'templates.wizard.buttons.hint': 'Buttons are the difference between a message people read and one they act on. Add up to ten.',
  'templates.wizard.buttons.count': '{{count}} of {{max}}',
  'templates.wizard.buttons.type': 'Does what',
  'templates.wizard.buttons.type.quick_reply': 'Replies with a set answer',
  'templates.wizard.buttons.type.url': 'Opens a link',
  'templates.wizard.buttons.type.phone_number': 'Calls a number',
  'templates.wizard.buttons.label': 'Label',
  'templates.wizard.buttons.label.hint': 'What the customer sees on the button. At most 25 characters.',
  'templates.wizard.buttons.url': 'Link',
  'templates.wizard.buttons.url.hint': 'The full address, starting with https://. Put {{1}} at the end if the last part changes per message.',
  'templates.wizard.buttons.phone': 'Number to call',
  'templates.wizard.buttons.phone.hint': 'In full international form, for example +27 82 123 4567.',
  'templates.wizard.buttons.remove': 'Remove this button',
  'templates.wizard.buttons.add.quick_reply': 'Add a reply button',
  'templates.wizard.buttons.add.url': 'Add a link button',
  'templates.wizard.buttons.add.phone_number': 'Add a call button',
  'templates.wizard.preview.business': 'Your business',
  'templates.wizard.preview.composer': 'Message',
  'templates.wizard.preview.empty': 'Your message will appear here.',
  'templates.wizard.preview.hint': 'This is what the customer sees, with your sample values filled in.',
  'templates.wizard.review.summary': 'What you are sending',
  'templates.wizard.review.clean': 'Nothing here would be refused. You can save this as a draft or send it to WhatsApp now.',
  'templates.wizard.review.problems': '{{count}} to fix before this can be sent',
  'templates.wizard.review.advice': 'Worth fixing, but it will not stop the template',
  'templates.wizard.review.metaLibrary': 'This is WhatsApp\'s own wording, unchanged, so it usually goes through review much faster than a template you write yourself.',
  'templates.wizard.review.submitWarning': 'Sending it to WhatsApp cannot be undone. The wording is fixed from that moment and review usually takes a day or so.',
  'templates.wizard.leave.title': 'Leave without saving?',
  'templates.wizard.leave.body': 'This template has not been saved. Leaving now loses everything you have typed.',
  'templates.wizard.leave.discard': 'Leave and lose it',
  'templates.wizard.leave.keep': 'Stay here',
  'templates.library.search.label': 'Search',
  'templates.library.search.placeholder': 'Order, delivery, payment…',
  'templates.library.category.any': 'Any category',
  'templates.library.loadError': 'The library could not be loaded. Try again in a moment.',
  'templates.library.metaUnavailable': 'WhatsApp is not offering its own library to this account, so what you see below was written by us. It works exactly the same way.',
  'templates.library.empty': 'Nothing matches',
  'templates.library.empty.hint': 'Try a different word, or clear the category.',
  'templates.library.source.meta': 'WhatsApp',
  'templates.library.source.starter': 'RepletoryReach starter',
  'templates.library.use': 'Use this one',
  'templates.library.chosen': 'Chosen',
  'templates.validate.buttonsEmpty': 'There is a buttons section with no buttons in it. Add one, or remove the section.',
  'templates.validate.buttonsTooMany': 'There are {{count}} buttons; WhatsApp allows {{max}}.',
  'templates.validate.buttonBadType': '\'{{type}}\' is not something a button can do.',
  'templates.validate.buttonTextEmpty': 'Every button needs a label.',
  'templates.validate.buttonTextTooLong': 'The button label \'{{text}}\' is {{length}} characters; WhatsApp allows {{max}}.',
  'templates.validate.buttonUrlMissing': 'A link button has no address. Add the address it should open.',
  'templates.validate.buttonPhoneMissing': 'A call button has no number. Add the number it should dial.',
  'templates.validate.buttonUrlRepeated': 'There are {{count}} link buttons; WhatsApp allows {{max}}.',
  'templates.validate.buttonPhoneRepeated': 'There are {{count}} call buttons; WhatsApp allows {{max}}.',
  'templates.validate.nameEmpty': 'The template needs a name.',
  'templates.validate.nameTooLong': 'The name is {{length}} characters; WhatsApp allows {{max}}.',
  'templates.validate.nameIllegal': 'A name may contain only lower-case letters, digits and underscores.',
  'templates.validate.samplesMissing': '{{have}} of the {{wanted}} placeholders has a sample value. WhatsApp reviews the template against the samples, so fill in a real example for each.',
  'templates.wizard.name.taken': 'You already have a template with this name in this language. Give this one a different name.',
  'templates.validate.buttonUrlPlaceholder': 'A link button still points at example.com. Put your own address in before you send this for review.'
};

export default templates;
